'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface CTACard {
  title: string;
  description: string;
  icon: string;
  buttonText: string;
  buttonLink?: string;
  isRFQ?: boolean;
  variant: 'primary' | 'secondary';
}

const CTASection = () => {
  const ctaCards: CTACard[] = [
    {
      title: 'Request a Quotation',
      description: 'Submit a detailed RFQ for precision manufacturing — covering materials, processes, tolerances, certifications, and delivery. Get a response within 24–48 hours.',
      icon: 'DocumentTextIcon',
      buttonText: 'REQUEST FOR QUOTATION',
      isRFQ: true,
      variant: 'primary',
    },
    {
      title: 'Explore Capabilities',
      description: 'Dive deep into our manufacturing services, certifications, and technical specifications to find the perfect fit.',
      icon: 'WrenchScrewdriverIcon',
      buttonText: 'VIEW CAPABILITIES',
      buttonLink: '/capabilities',
      variant: 'secondary',
    },
  ];

  return (
    <section className="relative py-24 bg-gradient-to-b from-background to-primary">
      <div className="mx-auto px-6 lg:px-8">
        {/* Main CTA Banner */}
        <div className="relative bg-gradient-to-r from-accent via-mission-orange to-accent rounded-3xl overflow-hidden mb-12">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.3) 1px, transparent 0)',
              backgroundSize: '30px 30px'
            }}></div>
          </div>
          
          <div className="relative px-8 py-16 lg:px-16 lg:py-20 text-center">
            <div className="max-w-4xl mx-auto space-y-6">
              <h2 className="font-headline text-4xl lg:text-6xl font-bold text-primary">
                Ready to Launch Your Next Mission?
              </h2>
              <p className="font-body text-xl text-primary/90 max-w-2xl mx-auto">
                Partner with Wings Bharath for mission-critical aerospace manufacturing that delivers precision, reliability, and innovation at the speed of your ambition.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                <Link
                  href="/rfq"
                  className="group relative inline-flex items-center justify-center space-x-3 px-10 py-4 font-cta text-base font-medium rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  style={{
                    background: 'linear-gradient(135deg, rgba(5,10,20,0.85) 0%, rgba(10,22,40,0.85) 100%)',
                    border: '1px solid transparent',
                  }}
                >
                  {/* Animated gradient border */}
                  <span className="absolute inset-0 rounded-lg" style={{
                    padding: '1px',
                    background: 'linear-gradient(135deg, #00d4ff 0%, #ee9572 50%, #00d4ff 100%)',
                    backgroundSize: '200% 200%',
                    animation: 'ctaRfqBorder 3s ease infinite',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }} />
                  <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-accent rounded-tl-lg" />
                  <span className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-accent rounded-tr-lg" />
                  <span className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-mission-orange rounded-bl-lg" />
                  <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-mission-orange rounded-br-lg" />
                  <span className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
                    <span className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/80 to-transparent"
                      style={{ animation: 'ctaRfqScan 2.5s ease-in-out infinite' }} />
                  </span>
                  <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: 'radial-gradient(ellipse at center, rgba(0,212,255,0.15) 0%, transparent 70%)', boxShadow: '0 0 30px rgba(0,212,255,0.3)' }} />
                  <span className="relative z-10 flex items-center space-x-3">
                    <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-70" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
                    </span>
                    <Icon name="DocumentTextIcon" size={20} className="text-accent group-hover:scale-110 transition-transform duration-300" />
                    <span className="text-accent tracking-widest">REQUEST FOR QUOTATION</span>
                    <span className="font-mono text-accent/60 text-xs group-hover:translate-x-1 transition-transform duration-300">▶▶</span>
                  </span>
                </Link>
                <Link
                  href="/support"
                  className="inline-flex items-center justify-center space-x-3 px-8 py-4 bg-transparent border-2 border-primary text-primary font-cta text-base font-medium rounded-lg hover:bg-primary/10 transition-all duration-300"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={20} />
                  <span>TALK TO EXPERT</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary CTAs */}
        <div className="grid md:grid-cols-2 gap-8">
          {ctaCards.map((card) => (
            <div
              key={card.title}
              className={`group relative bg-card border rounded-2xl p-8 hover:shadow-glow transition-all duration-300 ${
                card.variant === 'primary' ? 'border-accent' : 'border-border hover:border-accent'
              }`}
            >
              <div className="space-y-6">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                  card.variant === 'primary' ? 'bg-accent/20' : 'bg-muted'
                }`}>
                  <Icon
                    name={card.icon as any}
                    size={32}
                    className={card.variant === 'primary' ? 'text-accent' : 'text-technical-gray group-hover:text-accent transition-colors duration-300'}
                  />
                </div>
                
                <div>
                  <h3 className="font-headline text-2xl font-bold text-foreground mb-3">
                    {card.title}
                  </h3>
                  <p className="font-body text-technical-gray leading-relaxed">
                    {card.description}
                  </p>
                </div>

                {card.isRFQ ? (
                  <Link
                    href="/rfq"
                    className="group relative inline-flex items-center px-6 py-3 rounded-lg font-cta text-sm font-medium overflow-hidden transition-all duration-300 hover:-translate-y-0.5"
                    style={{
                      background: 'linear-gradient(135deg, rgba(0,212,255,0.06) 0%, rgba(238,149,114,0.06) 100%)',
                      border: '1px solid transparent',
                    }}
                  >
                    <span className="absolute inset-0 rounded-lg" style={{
                      padding: '1px',
                      background: 'linear-gradient(135deg, #00d4ff, #ee9572, #00d4ff)',
                      backgroundSize: '200% 200%',
                      animation: 'ctaRfqBorder 3s ease infinite',
                      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                    }} />
                    <span className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
                      <span className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent"
                        style={{ animation: 'ctaRfqScan 2.5s ease-in-out infinite' }} />
                    </span>
                    <span className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ boxShadow: '0 0 20px rgba(0,212,255,0.25), inset 0 0 15px rgba(0,212,255,0.05)' }} />
                    <span className="relative z-10 flex items-center space-x-2">
                      <span className="relative flex h-2 w-2 flex-shrink-0">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-70" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
                      </span>
                      <span className="text-accent tracking-widest">{card.buttonText}</span>
                      <span className="font-mono text-accent/60 text-[10px] group-hover:translate-x-0.5 transition-transform duration-300">▶</span>
                    </span>
                  </Link>
                ) : (
                  <Link
                    href={card.buttonLink!}
                    className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-cta text-sm font-medium transition-all duration-300 bg-transparent border-2 border-accent text-accent hover:bg-accent/10`}
                  >
                    <span>{card.buttonText}</span>
                    <Icon name="ArrowRightIcon" size={16} />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Trust Bar */}
        <div className="mt-16 text-center">
          <p className="font-body text-sm text-technical-gray mb-6">
            Trusted by leading aerospace, space, and defense organizations worldwide
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {['AS9100', 'ITAR', 'NADCAP', 'ISO 9001'].map((cert) => (
              <div
                key={cert}
                className="flex items-center space-x-2 px-4 py-2 bg-muted rounded-lg border border-border"
              >
                <Icon name="ShieldCheckIcon" size={20} className="text-aerospace-green" />
                <span className="font-cta text-sm text-technical-gray">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes ctaRfqBorder {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes ctaRfqScan {
          0% { top: -2px; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default CTASection;